import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import {
  login as handleLogin,
  refreshAccessToken,
  logout as handleLogout,
  revokeRefreshToken,
} from "services/authServices";
import { clearClassOptionCache } from "services/classOptionCache";
import { REFRESH_TOKEN_LOCAL_STORAGE } from "config/keywords";
import { LOGIN_PATH } from "router/pathName";
import { getCurrentLanguage } from "utilities/CurrentLanguageGetter";
import type { RootState } from "store";

const login = createAsyncThunk<
  { token: string },
  { loginKey: string; password: string },
  { rejectValue: string }
>("auth/login", async ({ loginKey, password }, { rejectWithValue }) => {
  try {
    clearClassOptionCache();
    const authToken = await handleLogin(
      loginKey,
      password,
      getCurrentLanguage()
    );
    localStorage.setItem(REFRESH_TOKEN_LOCAL_STORAGE, authToken.refresh);
    return { token: authToken.access };
  } catch (e: any) {
    if (axios.isAxiosError(e)) {
      const errorMessage = e.response?.data.detail;
      return rejectWithValue(errorMessage || "_generic._unknown_error");
    } else {
      return rejectWithValue(e.message);
    }
  }
});

const refreshToken = createAsyncThunk<
  { token: string },
  void,
  { rejectValue: string }
>("auth/refreshToken", async (_: void, { rejectWithValue }) => {
  try {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_LOCAL_STORAGE);
    const newAuthToken = await refreshAccessToken(refreshToken || "");
    localStorage.setItem(REFRESH_TOKEN_LOCAL_STORAGE, newAuthToken.refresh);
    return { token: newAuthToken.access };
  } catch (e) {
    localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE);
    window.location.replace(LOGIN_PATH);
    return rejectWithValue("_generic._unknown_error");
  }
});

const logout = createAsyncThunk<void, void, { rejectValue: string }>(
  "auth/logout",
  async (_: void, { rejectWithValue }) => {
    try {
      clearClassOptionCache();
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_LOCAL_STORAGE);
      if (refreshToken) {
        await handleLogout(refreshToken || "");
        localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE);
      } else {
        throw new Error();
      }
    } catch (e) {
      return rejectWithValue("_generic._unknown_error");
    }
  }
);

// Ends the session of an account that signed in but may not use this app
// (owner, guardian, family). Unlike logout it doesn't reload the page, so the
// error message on the login screen stays.
const discardSession = createAsyncThunk<void, void>(
  "auth/discardSession",
  async (_: void, { getState }) => {
    clearClassOptionCache();
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_LOCAL_STORAGE);
    if (!refreshToken) return;
    // Remove it before the request: if someone logs in while the revoke is in
    // flight, the token they store must survive.
    localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE);
    try {
      await revokeRefreshToken(
        refreshToken,
        (getState() as RootState).auth.token
      );
    } catch (e) {
      // The token is already gone from this browser; if the revoke failed it
      // just expires on the server.
    }
  }
);

export { login, refreshToken, logout, discardSession };
